import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";

const toStr = (d) => d.toLocaleDateString("en-CA");
const parse = (s) => new Date(`${s}T00:00:00`);
const WEEKS = 26;

function weekStart(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}

function buildWeeks(anchor) {
  const start = weekStart(anchor);
  start.setDate(start.getDate() - WEEKS * 7);
  return Array.from({ length: WEEKS * 2 + 1 }, (_, w) => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + w * 7 + i);
    return d;
  }));
}

// Always-visible, swipeable week calendar. value: "YYYY-MM-DD" or "" (none selected).
// preview(date) => Promise<[{id, title, sub}]> enables press-and-hold preview on marked days.
export function DayStrip({ value, onChange, max, testid = "day-strip", clearLabel, marked = [], countLabel = "entries", badgeTone = "bg-violet-600 text-white", preview }) {
  const today = toStr(new Date());
  const [peek, setPeek] = useState(null);
  const hold = useRef({ t: null, fired: false });
  const startHold = (s) => {
    hold.current.fired = false;
    clearTimeout(hold.current.t);
    hold.current.t = setTimeout(() => {
      hold.current.fired = true;
      if (navigator.vibrate) navigator.vibrate(15);
      setPeek({ date: s, items: null });
      Promise.resolve(preview(s)).then((items) => setPeek((p) => (p && p.date === s ? { date: s, items: items || [] } : p))).catch(() => setPeek((p) => (p && p.date === s ? { date: s, items: [] } : p)));
    }, 500);
  };
  const endHold = () => clearTimeout(hold.current.t);
  const counts = useMemo(() => {
    if (!Array.isArray(marked)) return marked || {};
    const c = {};
    marked.forEach((d) => { c[d] = (c[d] || 0) + 1; });
    return c;
  }, [marked]);
  const hasAny = Object.keys(counts).length > 0;
  const [anchor, setAnchor] = useState(() => parse(value || today));
  const weeks = useMemo(() => buildWeeks(anchor), [anchor]);
  const ref = useRef(null);
  const [visible, setVisible] = useState(WEEKS);
  const first = useRef(true);

  useEffect(() => {
    const target = value || today;
    let idx = weeks.findIndex((w) => toStr(w[0]) <= target && target <= toStr(w[6]));
    if (idx < 0) { setAnchor(parse(target)); return; }
    const el = ref.current;
    if (el) el.scrollTo({ left: idx * el.clientWidth, behavior: first.current ? "auto" : "smooth" });
    first.current = false;
  }, [value, weeks]); // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = () => { const el = ref.current; if (el) setVisible(Math.round(el.scrollLeft / el.clientWidth)); };
  const step = (n) => { const el = ref.current; if (el) el.scrollBy({ left: n * el.clientWidth, behavior: "smooth" }); };
  const wk = weeks[visible] || weeks[WEEKS];
  const label = wk[0].toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div data-testid={testid} className="bg-white rounded-2xl shadow-sm p-3 select-none">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span data-testid={`${testid}-month`} className="font-display font-bold text-slate-900 text-sm">{label}</span>
        <span className="flex-1 text-[11px] text-slate-400 flex items-center gap-1">{hasAny && <><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> has entries <span className={`ml-1 px-1.5 rounded-full text-[10px] font-bold ${badgeTone}`}>n</span> {countLabel}</>}</span>
        {clearLabel && <button data-testid={`${testid}-clear`} onClick={() => onChange("")} className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${!value ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}>{clearLabel}</button>}
        <button data-testid={`${testid}-today`} onClick={() => onChange(today)} className="text-xs font-semibold px-3 py-1 rounded-full bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors">Today</button>
        <button data-testid={`${testid}-prev`} onClick={() => step(-1)} className="p-1.5 rounded-full hover:bg-violet-50 text-violet-700"><ChevronLeft size={18} /></button>
        <button data-testid={`${testid}-next`} onClick={() => step(1)} className="p-1.5 rounded-full hover:bg-violet-50 text-violet-700"><ChevronRight size={18} /></button>
      </div>
      <div ref={ref} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {weeks.map((w) => (
          <div key={toStr(w[0])} className="grid grid-cols-7 gap-1.5 flex-none w-full snap-start px-0.5">
            {w.map((d) => {
              const s = toStr(d);
              const sel = s === value;
              const off = max && s > max;
              const dot = s in counts;
              const n = counts[s] || 0;
              const canPeek = dot && !!preview && !off;
              return (
                <button key={s} data-testid={`${testid}-day-${s}`} disabled={off} title={canPeek ? "Press and hold to preview" : undefined}
                  onClick={() => { if (hold.current.fired) { hold.current.fired = false; return; } onChange(s); }}
                  onPointerDown={canPeek ? () => startHold(s) : undefined} onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold}
                  onContextMenu={canPeek ? (e) => e.preventDefault() : undefined}
                  className={`relative flex flex-col items-center rounded-xl py-2 transition-[background-color,transform,color] duration-200 ${sel ? "bg-violet-600 text-white shadow-md scale-105" : off ? "text-slate-300" : "text-slate-700 hover:bg-violet-50 active:scale-95"} ${s === today && !sel ? "ring-1 ring-violet-300" : ""}`}>
                  {n > 0 && <span data-testid={`${testid}-count-${s}`} className={`absolute -top-1 left-1/2 ml-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-[18px] text-center shadow-sm ${sel ? "bg-white text-violet-700" : badgeTone}`}>{n > 99 ? "99+" : n}</span>}
                  <span className={`text-[10px] uppercase tracking-wide ${sel ? "text-white/80" : "text-slate-400"}`}>{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                  <span className="font-display text-lg font-bold leading-tight">{d.getDate()}</span>
                  <span data-testid={dot ? `${testid}-dot-${s}` : undefined} className={`h-1.5 w-1.5 rounded-full mt-0.5 ${dot ? (sel ? "bg-white" : "bg-emerald-500") : "bg-transparent"}`} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {peek && <PeekPanel peek={peek} testid={testid} onClose={() => setPeek(null)} onOpen={() => { onChange(peek.date); setPeek(null); }} />}
    </div>
  );
}

function PeekPanel({ peek, testid, onClose, onOpen }) {
  const label = parse(peek.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" });
  return (
    <div data-testid={`${testid}-preview`} className="mt-3 rounded-xl border border-violet-100 bg-violet-50/60 p-3 animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-display font-bold text-sm text-slate-900 flex-1">{label}</span>
        <button data-testid={`${testid}-preview-open`} onClick={onOpen} className="text-xs font-semibold px-3 py-1 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors">Open this day</button>
        <button data-testid={`${testid}-preview-close`} onClick={onClose} className="p-1 rounded-full text-slate-400 hover:text-slate-700"><X size={16} /></button>
      </div>
      {!peek.items ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        : peek.items.length === 0 ? <div className="text-sm text-slate-500">Nothing to show for this day.</div>
        : <ul className="space-y-1 max-h-48 overflow-y-auto">{peek.items.slice(0, 20).map((x, i) => (
          <li key={x.id || i} data-testid={`${testid}-preview-item-${i}`} className="text-sm"><span className="font-semibold text-slate-800">{x.title}</span>{x.sub && <span className="text-xs text-slate-500"> • {x.sub}</span>}</li>
        ))}{peek.items.length > 20 && <li className="text-xs text-slate-500">+{peek.items.length - 20} more</li>}</ul>}
    </div>
  );
}
